import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TRIAL_DAYS } from "../../../constants/trial";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import PunchMatteImage from "./PunchMatteImage";
import { useDomPin } from "./useDomPin";
import s from "./createAppLanding.module.css";

const BRAND_SHOTS = [
  {
    id: "splash",
    src: "/landing/mock-brand-splash.png",
    alt: "Splash «Тигр»",
    label: "Экран запуска",
  },
  {
    id: "club",
    src: "/landing/mock-brand-club.png",
    alt: "Профиль клуба",
    label: "Профиль клуба",
  },
  {
    id: "manage",
    src: "/landing/mock-brand-manage.png",
    alt: "Кабинет клуба",
    label: "Кабинет клуба",
  },
];

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Fan open → fly left one-by-one (top layer first) → brand finale.
 */
export default function PhoneMockupSections() {
  const railRef = useRef(null);
  const panelRef = useRef(null);
  const phoneRefs = useRef([]);
  const headRef = useRef(null);
  const stageRef = useRef(null);
  const finaleRef = useRef(null);
  const captionRef = useRef(null);
  const [label, setLabel] = useState(BRAND_SHOTS[0].label);
  const labelIdx = useRef(0);

  useDomPin(railRef, panelRef, (p) => {
    const n = BRAND_SHOTS.length;
    const mobile = window.innerWidth < 700;
    // 0..0.16 fan · 0.16..0.72 fly · 0.72..1 finale
    const open = easeInOut(Math.min(1, p / 0.16));
    const flyPhase = Math.min(1, Math.max(0, (p - 0.16) / 0.56));
    const finale = easeInOut(Math.min(1, Math.max(0, (p - 0.72) / 0.28)));

    let front = Math.min(n - 1, Math.floor(flyPhase * n - 1e-9));
    if (flyPhase <= 0) front = 0;
    if (finale > 0.15) front = n - 1;

    phoneRefs.current.forEach((el, i) => {
      if (!el) return;
      const slot = i - (n - 1) / 2;
      const fanX = slot * open * (mobile ? 28 : 38);
      const fanRot = slot * open * (mobile ? 10 : 14);
      const fanY = (1 - open) * Math.abs(slot) * (mobile ? 6 : 10);
      const fanScale = 0.92 + open * 0.08;

      const seg = 1 / n;
      const local = Math.min(1, Math.max(0, (flyPhase - i * seg) / seg));
      const fly = easeOutCubic(local);
      const flying = local > 0 && local < 1;
      const gone = local >= 1;

      let z = (n - i) * 10;
      if (flying) z = 100;
      if (gone) z = 0;

      const flyX = -fly * (mobile ? 140 : 175);
      const flyRot = -fly * (mobile ? 18 : 24);
      const flyY = -fly * 6;
      const opacity = gone
        ? 0
        : fly > 0.6
          ? Math.max(0, 1 - (fly - 0.6) / 0.4)
          : 1;

      el.style.opacity = String(opacity * (1 - finale));
      el.style.zIndex = String(z);
      el.style.visibility =
        opacity < 0.02 || finale > 0.85 ? "hidden" : "visible";
      el.style.transform = `translate(-50%, -50%) translate3d(${fanX + flyX}%, ${fanY + flyY}px, 0) rotate(${fanRot + flyRot}deg) scale(${fanScale})`;
    });

    if (headRef.current) {
      // On mobile: opacity only — scale/translate shakes with address-bar resize
      headRef.current.style.opacity = String(1 - finale * 0.95);
      if (mobile) {
        headRef.current.style.transform = "";
      } else {
        headRef.current.style.transform = `translate3d(0, ${-finale * 36}px, 0) scale(${1 - finale * 0.08})`;
      }
    }
    if (stageRef.current) {
      stageRef.current.style.opacity = String(Math.max(0, 1 - finale * 1.2));
    }
    if (captionRef.current) {
      captionRef.current.style.opacity = String(Math.max(0, 1 - finale * 1.4));
    }
    if (finaleRef.current) {
      const show = finale;
      finaleRef.current.style.opacity = String(show);
      finaleRef.current.style.pointerEvents = show > 0.4 ? "auto" : "none";
      if (mobile) {
        // Fade only once mostly visible — avoid continuous scale jitter
        finaleRef.current.style.transform = "translate(-50%, -50%)";
      } else {
        finaleRef.current.style.transform = `translate(-50%, -50%) scale(${0.86 + show * 0.14}) translate3d(0, ${(1 - show) * 40}px, 0)`;
      }
    }

    if (front !== labelIdx.current && finale < 0.2) {
      labelIdx.current = front;
      setLabel(BRAND_SHOTS[front].label);
    }
  });

  return (
    <section
      id="showcase"
      ref={railRef}
      className={s.fanRail}
      aria-label="Брендинг клуба"
    >
      <div ref={panelRef} className={s.fanInner}>
        <div ref={headRef} className={s.fanHead}>
          <p className={s.fanEyebrow}>Бренд</p>
          <h2 className={s.fanTitle}>Ваш логотип и название в приложении</h2>
        </div>

        <div ref={stageRef} className={s.fanStage}>
          {BRAND_SHOTS.map((shot, i) => (
            <figure
              key={shot.id}
              ref={(el) => {
                phoneRefs.current[i] = el;
              }}
              className={s.fanPhone}
            >
              <PunchMatteImage
                src={shot.src}
                alt={shot.alt}
                className={s.fanPhoneImg}
              />
            </figure>
          ))}
        </div>

        <p ref={captionRef} className={s.fanCaption} aria-live="polite">
          {label}
        </p>

        <div ref={finaleRef} className={s.fanFinale}>
          <div className={s.fanFinaleGlow} />
          <p className={s.fanFinaleEyebrow}>Бренд клуба</p>
          <p className={s.fanFinaleTitle}>
            Логотип.
            <br />
            Цвета.
            <br />
            Название.
          </p>
          <p className={s.fanFinaleSub}>
            Клиент сразу видит, что это приложение вашего клуба.
          </p>
          <Link
            to="/register-clinic"
            className={s.fanFinaleCta}
            onClick={() => trackTryClick({ landing: "app" })}
          >
            Попробовать {TRIAL_DAYS} дней
          </Link>
        </div>
      </div>
    </section>
  );
}
